import React, { useState, useEffect, useCallback } from 'react';
import { useLanguage } from '../../contexts/LanguageContext';
import { theme, pageTitleStyle, pageTitleTextStyle, primaryButtonStyle } from '../../theme';

const BASE = '/api';
function authFetch(url, opts = {}) {
  const token = localStorage.getItem('arcus_token');
  return fetch(url, { ...opts, headers: { ...(opts.headers || {}), ...(token ? { Authorization: `Bearer ${token}` } : {}) } });
}

const ORANGE = theme.orange;
const SvgIcon = ({ size = 16, color = 'currentColor', children }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none"
    stroke={color} strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}>
    {children}
  </svg>
);

const CATS = ['gf', 'rc_pro', 'commun', 'sinistre'];
const CAT_COLOR = {
  gf:       { bg: '#FEF3E2', color: '#9A5A00' },
  rc_pro:   { bg: '#E6F1FB', color: '#185FA5' },
  commun:   { bg: '#F1F1EE', color: '#5F5E5A' },
  sinistre: { bg: '#FCEBEB', color: '#A32D2D' },
};
const fmtSize = b => !b ? '' : b < 1024 ? `${b} o` : b < 1048576 ? `${(b / 1024).toFixed(0)} Ko` : `${(b / 1048576).toFixed(1)} Mo`;

export default function GestionDocumentaire() {
  const { t, lang } = useLanguage();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const [uploadFor, setUploadFor] = useState(null);   // template en cours d'upload
  const [file, setFile] = useState(null);
  const [notes, setNotes] = useState('');
  const [uploading, setUploading] = useState(false);
  const [dragOver, setDragOver] = useState(false);

  const [historyFor, setHistoryFor] = useState(null); // template id (historique ouvert)
  const [versions, setVersions] = useState([]);

  const [showAdd, setShowAdd] = useState(false);
  const [addForm, setAddForm] = useState({ nom: '', categorie: 'commun', description: '' });

  const fmtDate = iso => { try { return new Date(iso).toLocaleDateString(lang === 'fr' ? 'fr-FR' : 'en-GB', { day: '2-digit', month: 'short', year: 'numeric' }); } catch { return ''; } };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await authFetch(`${BASE}/templates`);
      const d = await r.json();
      if (!r.ok) throw new Error(d.error || 'Erreur');
      setRows(Array.isArray(d) ? d : []);
      setError('');
    } catch (e) { setError(e.message); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  async function doUpload() {
    if (!file || !uploadFor) return;
    setUploading(true); setError('');
    try {
      const fd = new FormData();
      fd.append('fichier', file);
      if (notes) fd.append('notes', notes);
      const r = await authFetch(`${BASE}/templates/${uploadFor.id}/versions`, { method: 'POST', body: fd });
      const d = await r.json();
      if (!r.ok) throw new Error(d.error || 'Erreur');
      setUploadFor(null); setFile(null); setNotes('');
      await load();
    } catch (e) { setError(e.message); }
    finally { setUploading(false); }
  }

  async function download(url, filename) {
    try {
      const r = await authFetch(url);
      if (!r.ok) throw new Error('Téléchargement impossible');
      const blob = await r.blob();
      const a = document.createElement('a');
      a.href = URL.createObjectURL(blob);
      a.download = filename || 'document';
      document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => URL.revokeObjectURL(a.href), 2000);
    } catch (e) { setError(e.message); }
  }

  async function openHistory(tid) {
    if (historyFor === tid) { setHistoryFor(null); return; }
    setHistoryFor(tid); setVersions([]);
    try {
      const r = await authFetch(`${BASE}/templates/${tid}/versions`);
      const d = await r.json();
      if (r.ok) setVersions(Array.isArray(d) ? d : []);
    } catch (_) {}
  }

  async function addTemplate() {
    if (!addForm.nom.trim()) return;
    try {
      const r = await authFetch(`${BASE}/templates`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(addForm),
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d.error || 'Erreur');
      setShowAdd(false); setAddForm({ nom: '', categorie: 'commun', description: '' });
      await load();
    } catch (e) { setError(e.message); }
  }

  async function toggleActif(tpl) {
    try {
      await authFetch(`${BASE}/templates/${tpl.id}`, {
        method: 'PATCH', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ actif: !tpl.actif }),
      });
      await load();
    } catch (e) { setError(e.message); }
  }

  async function deleteTemplate(tpl) {
    if (!window.confirm(t('gestiondoc.delete_confirm'))) return;
    try {
      const r = await authFetch(`${BASE}/templates/${tpl.id}`, { method: 'DELETE' });
      const d = await r.json();
      if (!r.ok) throw new Error(d.error || 'Erreur');
      await load();
    } catch (e) { setError(e.message); }
  }

  const byCat = c => rows.filter(r => r.categorie === c);

  return (
    <div style={{ maxWidth: 980, margin: '0 auto', padding: '0 4px 40px' }}>
      <div style={{ ...pageTitleStyle, justifyContent: 'space-between' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <SvgIcon size={22} color={ORANGE}><path d="M14 3v4a1 1 0 0 0 1 1h4M17 21h-10a2 2 0 0 1 -2 -2v-14a2 2 0 0 1 2 -2h7l5 5v11a2 2 0 0 1 -2 2z" /></SvgIcon>
          <div>
            <div style={pageTitleTextStyle}>{t('gestiondoc.title')}</div>
            <div style={{ fontSize: 13, color: theme.textSecondary, marginTop: 2 }}>{t('gestiondoc.subtitle')}</div>
          </div>
        </div>
        <button onClick={() => setShowAdd(true)} style={{ ...primaryButtonStyle, display: 'inline-flex', alignItems: 'center', gap: 8 }}>
          <SvgIcon size={16} color="#fff"><path d="M12 5v14M5 12h14" /></SvgIcon>{t('gestiondoc.add_template')}
        </button>
      </div>

      {error && <div style={{ background: '#FCEBEB', border: '1px solid #F5C6C6', color: '#A32D2D', borderRadius: 8, padding: '10px 14px', margin: '12px 0', fontSize: 13 }}>⚠ {error}</div>}
      {loading && <div style={{ padding: 30, textAlign: 'center', color: theme.textTertiary }}>…</div>}

      {!loading && CATS.map(cat => {
        const list = byCat(cat);
        if (!list.length) return null;
        return (
          <div key={cat} style={{ marginTop: 22 }}>
            <div style={{ fontSize: 12, fontWeight: 700, color: theme.textTertiary, textTransform: 'uppercase', letterSpacing: '.05em', marginBottom: 10 }}>
              {t('gestiondoc.cat_' + cat)}
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              {list.map(tpl => (
                <div key={tpl.id} style={{ background: '#fff', border: `1px solid ${theme.border}`, borderRadius: 12, overflow: 'hidden', opacity: tpl.actif ? 1 : 0.6 }}>
                  <div style={{ padding: '14px 16px', display: 'flex', alignItems: 'center', gap: 14, flexWrap: 'wrap' }}>
                    <div style={{ flex: 1, minWidth: 220 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                        <span style={{ fontSize: 14.5, fontWeight: 700, color: theme.text }}>{tpl.nom}</span>
                        <span style={{ fontSize: 10, fontWeight: 700, padding: '2px 8px', borderRadius: 99, background: CAT_COLOR[cat]?.bg, color: CAT_COLOR[cat]?.color }}>
                          {tpl.systeme ? t('gestiondoc.systeme_tag') : t('gestiondoc.custom_tag')}
                        </span>
                        {!tpl.actif && <span style={{ fontSize: 10, fontWeight: 700, padding: '2px 8px', borderRadius: 99, background: '#F1F1EE', color: '#5F5E5A' }}>{t('gestiondoc.inactive')}</span>}
                      </div>
                      {tpl.description && <div style={{ fontSize: 12, color: theme.textSecondary, marginTop: 3 }}>{tpl.description}</div>}
                      <div style={{ fontSize: 12, marginTop: 6 }}>
                        {tpl.version_id
                          ? <span style={{ color: '#0F6E56' }}>
                              <strong>v{tpl.version}</strong> · {tpl.fichier_nom} <span style={{ color: theme.textTertiary }}>({fmtSize(tpl.fichier_taille)})</span> · {fmtDate(tpl.uploaded_at)}{tpl.uploaded_by_nom ? ` · ${tpl.uploaded_by_nom}` : ''}
                            </span>
                          : <span style={{ color: theme.textTertiary }}>{t('gestiondoc.no_file')}</span>}
                      </div>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                      {tpl.version_id && (
                        <button onClick={() => download(`${BASE}/templates/${tpl.id}/download`, tpl.fichier_nom)}
                          style={btnGhost}>
                          <SvgIcon size={15}><path d="M4 17v2a2 2 0 0 0 2 2h12a2 2 0 0 0 2 -2v-2M7 11l5 5l5 -5M12 4v12" /></SvgIcon>{t('gestiondoc.download')}
                        </button>
                      )}
                      <button onClick={() => { setUploadFor(tpl); setFile(null); setNotes(''); }} style={btnPrimary}>
                        <SvgIcon size={15} color="#fff"><path d="M4 17v2a2 2 0 0 0 2 2h12a2 2 0 0 0 2 -2v-2M7 9l5 -5l5 5M12 4v12" /></SvgIcon>{t('gestiondoc.upload_new')}
                      </button>
                      {tpl.nb_versions > 0 && (
                        <button onClick={() => openHistory(tpl.id)} style={btnGhost}>{t('gestiondoc.history')} ({tpl.nb_versions})</button>
                      )}
                      <button onClick={() => toggleActif(tpl)} title={tpl.actif ? t('gestiondoc.inactive') : t('gestiondoc.active')} style={btnIcon}>
                        <SvgIcon size={16} color={theme.textTertiary}>{tpl.actif ? <path d="M18 6L6 18M6 6l12 12" /> : <path d="M5 12l5 5l10 -10" />}</SvgIcon>
                      </button>
                      {!tpl.systeme && (
                        <button onClick={() => deleteTemplate(tpl)} style={btnIcon}>
                          <SvgIcon size={16} color="#DC2626"><path d="M4 7h16M10 11v6M14 11v6M5 7l1 12a2 2 0 0 0 2 2h8a2 2 0 0 0 2 -2l1 -12M9 7V4a1 1 0 0 1 1 -1h4a1 1 0 0 1 1 1v3" /></SvgIcon>
                        </button>
                      )}
                    </div>
                  </div>

                  {historyFor === tpl.id && (
                    <div style={{ background: theme.surfaceAlt, borderTop: `1px solid ${theme.border}`, padding: '10px 16px' }}>
                      <div style={{ fontSize: 11.5, fontWeight: 700, color: theme.textSecondary, marginBottom: 8, textTransform: 'uppercase', letterSpacing: '.04em' }}>{t('gestiondoc.hist_title')}</div>
                      {versions.length === 0 ? <div style={{ fontSize: 12.5, color: theme.textTertiary }}>—</div> : versions.map(v => (
                        <div key={v.id} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '6px 0', borderTop: `1px solid ${theme.border}`, fontSize: 12.5, flexWrap: 'wrap' }}>
                          <strong style={{ color: theme.text, minWidth: 34 }}>v{v.version}</strong>
                          {v.est_courante && <span style={{ fontSize: 10, fontWeight: 700, padding: '1px 7px', borderRadius: 99, background: '#D1FAE5', color: '#065F46' }}>{t('gestiondoc.current_tag')}</span>}
                          <span style={{ color: theme.textSecondary }}>{v.fichier_nom} <span style={{ color: theme.textTertiary }}>({fmtSize(v.fichier_taille)})</span></span>
                          <span style={{ color: theme.textTertiary }}>· {fmtDate(v.uploaded_at)}{v.uploaded_by_nom ? ` · ${v.uploaded_by_nom}` : ''}</span>
                          {v.notes && <span style={{ color: theme.textTertiary, fontStyle: 'italic' }}>— {v.notes}</span>}
                          <button onClick={() => download(`${BASE}/templates/versions/${v.id}/download`, v.fichier_nom)} style={{ ...btnGhost, marginLeft: 'auto', padding: '3px 10px', fontSize: 12 }}>{t('gestiondoc.download')}</button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        );
      })}

      {!loading && rows.length === 0 && <div style={{ padding: 40, textAlign: 'center', color: theme.textTertiary }}>{t('gestiondoc.empty')}</div>}

      {/* Modale upload */}
      {uploadFor && (
        <div onClick={() => !uploading && setUploadFor(null)} style={overlay}>
          <div onClick={e => e.stopPropagation()} style={{ ...modalCard, width: 460 }}>
            <div style={{ fontSize: 16, fontWeight: 700, marginBottom: 4, color: theme.text }}>{t('gestiondoc.modal_upload_title')}</div>
            <div style={{ fontSize: 13, color: theme.textSecondary, marginBottom: 16 }}>{uploadFor.nom}</div>
            <label
              onDragOver={e => { e.preventDefault(); setDragOver(true); }}
              onDragLeave={() => setDragOver(false)}
              onDrop={e => { e.preventDefault(); setDragOver(false); if (e.dataTransfer.files[0]) setFile(e.dataTransfer.files[0]); }}
              style={{ display: 'block', border: `1.5px dashed ${dragOver ? ORANGE : theme.border}`, borderRadius: 10, padding: '20px', textAlign: 'center', cursor: 'pointer', background: dragOver ? '#FFF7F2' : theme.surfaceAlt, marginBottom: 14 }}>
              <input type="file" accept=".docx,.doc,.pdf" style={{ display: 'none' }} onChange={e => setFile(e.target.files[0] || null)} />
              {file
                ? <div style={{ fontSize: 13, color: theme.text, fontWeight: 600 }}>{file.name} <span style={{ color: theme.textTertiary, fontWeight: 400 }}>({fmtSize(file.size)})</span></div>
                : <div style={{ fontSize: 13, color: theme.textSecondary }}>{t('gestiondoc.drop_hint')}</div>}
              <div style={{ fontSize: 11, color: theme.textTertiary, marginTop: 6 }}>{t('gestiondoc.modal_file')}</div>
            </label>
            <label style={lbl}>{t('gestiondoc.modal_notes')}</label>
            <input value={notes} onChange={e => setNotes(e.target.value)} style={inp} placeholder="…" />
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop: 18 }}>
              <button onClick={() => setUploadFor(null)} disabled={uploading} style={btnGhost}>{t('common.cancel')}</button>
              <button onClick={doUpload} disabled={uploading || !file} style={{ ...btnPrimary, opacity: (uploading || !file) ? 0.5 : 1 }}>
                {uploading ? '…' : t('gestiondoc.modal_upload_btn')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modale ajout de modèle */}
      {showAdd && (
        <div onClick={() => setShowAdd(false)} style={overlay}>
          <div onClick={e => e.stopPropagation()} style={{ ...modalCard, width: 440 }}>
            <div style={{ fontSize: 16, fontWeight: 700, marginBottom: 14, color: theme.text }}>{t('gestiondoc.add_title')}</div>
            <label style={lbl}>{t('gestiondoc.add_nom')}</label>
            <input value={addForm.nom} onChange={e => setAddForm(f => ({ ...f, nom: e.target.value }))} style={inp} />
            <label style={{ ...lbl, marginTop: 12 }}>{t('gestiondoc.add_categorie')}</label>
            <select value={addForm.categorie} onChange={e => setAddForm(f => ({ ...f, categorie: e.target.value }))} style={inp}>
              {CATS.map(c => <option key={c} value={c}>{t('gestiondoc.cat_' + c)}</option>)}
            </select>
            <label style={{ ...lbl, marginTop: 12 }}>{t('gestiondoc.add_desc')}</label>
            <input value={addForm.description} onChange={e => setAddForm(f => ({ ...f, description: e.target.value }))} style={inp} />
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop: 18 }}>
              <button onClick={() => setShowAdd(false)} style={btnGhost}>{t('common.cancel')}</button>
              <button onClick={addTemplate} disabled={!addForm.nom.trim()} style={{ ...btnPrimary, opacity: addForm.nom.trim() ? 1 : 0.5 }}>{t('gestiondoc.add_btn')}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

const btnPrimary = { fontSize: 13, padding: '8px 14px', borderRadius: 8, background: theme.orange, color: '#fff', border: 'none', cursor: 'pointer', fontWeight: 700, display: 'inline-flex', alignItems: 'center', gap: 7 };
const btnGhost   = { fontSize: 13, padding: '8px 14px', borderRadius: 8, background: '#fff', color: theme.textSecondary, border: `1px solid ${theme.border}`, cursor: 'pointer', fontWeight: 600, display: 'inline-flex', alignItems: 'center', gap: 7 };
const btnIcon    = { padding: '7px', borderRadius: 8, background: '#fff', border: `1px solid ${theme.border}`, cursor: 'pointer', display: 'inline-flex', alignItems: 'center' };
const overlay    = { position: 'fixed', inset: 0, background: 'rgba(0,0,0,.45)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1200 };
const modalCard  = { background: '#fff', borderRadius: 12, padding: 24, maxWidth: '92vw' };
const lbl        = { fontSize: 12, color: theme.textSecondary, fontWeight: 600, display: 'block', marginBottom: 4 };
const inp        = { width: '100%', border: `1px solid ${theme.border}`, borderRadius: 8, padding: '8px 10px', fontSize: 13, boxSizing: 'border-box', fontFamily: 'inherit' };
